/**
 * EvidenceLinksSection — where a candidate came from: the source, its fragment classification and the daemon runs that
 * found and reviewed it. Demo page: /ui-elements/cards.
 */
import { sentenceCase } from '../../../components/ui/FilterSelect.js';
import { Link } from 'react-router-dom';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { Badge } from '../../../components/ui/index.js';
import { safeHref } from '../../../lib/safe-url.js';
import { daemonRunPath } from '../catalogReviewLinks.js';
import type { CatalogCandidateDecision } from './catalog-types.js';
import {
  buildCatalogSourceEvidenceHref,
  describeCatalogCandidateIntegrity,
  findCatalogSourceFragment,
} from './catalog-helpers.js';

export function EvidenceLinksSection({ candidate }: { candidate: CatalogCandidateDecision }) {
  const evidence = candidate.evidenceLinks;
  const sourceUrl = buildCatalogSourceEvidenceHref(
    evidence
      ? {
          sourceUrl: evidence.sourceUrl,
          sourceRepoUrl: evidence.sourceRepoUrl,
        }
      : null,
    candidate.source,
  );
  const canonicalKey = evidence?.canonicalKey ?? candidate.source?.canonicalKey;
  const sourcePath = evidence?.sourcePath ?? candidate.source?.sourcePath;
  const sourceRef = evidence?.sourceRef ?? candidate.source?.sourceRef;
  const sourceFragment = findCatalogSourceFragment(candidate);
  const integrityState = describeCatalogCandidateIntegrity(candidate);
  const sourceEvidenceHref = buildCatalogSourceEvidenceHref(evidence, candidate.source);
  const runLinks = [
    { label: 'Discovery run', run: evidence?.discoveryRun },
    { label: 'Security review run', run: evidence?.securityReviewRun },
    { label: 'Quality review run', run: evidence?.qualityReviewRun },
  ].filter((item): item is { label: string; run: NonNullable<typeof item.run> } => Boolean(item.run));

  return (
    <section className="rounded-lg border bg-surface-alt p-4">
      <h3 className="text-sm font-semibold text-heading">Evidence And Provenance</h3>
      <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3 text-sm">
        <div className="rounded-sm border bg-surface p-3">
          <div className="text-xs font-medium text-muted">Source</div>
          <div className="mt-1 text-heading wrap-break-word">
            {candidate.source?.title ??
              candidate.source?.packageName ??
              candidate.source?.provider ??
              'Recorded source'}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {sourceFragment?.classification && (
              <Badge
                variant={
                  sourceFragment.classification === 'library_candidate'
                    ? 'success'
                    : sourceFragment.classification === 'source_only'
                      ? 'warning'
                      : 'neutral'
                }
              >
                {sentenceCase(sourceFragment.classification)}
              </Badge>
            )}
            {integrityState.hasBlockingChecks && <Badge variant="danger">needs extraction repair</Badge>}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {sourceEvidenceHref && (
              <a
                href={safeHref(sourceEvidenceHref) ?? undefined}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-accent-text hover:underline"
              >
                Open source evidence <ExternalLink size={13} />
              </a>
            )}
            {sourceUrl && sourceUrl !== sourceEvidenceHref && (
              <a
                href={safeHref(sourceUrl) ?? undefined}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-accent-text hover:underline"
              >
                Open source provenance <ExternalLink size={13} />
              </a>
            )}
          </div>
          {sourceFragment?.rationale && <p className="mt-3 text-xs text-muted">{sourceFragment.rationale}</p>}
          <details className="mt-3 text-xs text-muted">
            <summary className="cursor-pointer hover:text-heading">Technical source identity</summary>
            <div className="mt-2 space-y-1 break-all">
              <div>{canonicalKey ?? `Source ${candidate.sourceId}`}</div>
              {sourceRef && <div>Ref: {sourceRef}</div>}
              {sourcePath && <div>Path: {sourcePath}</div>}
              {sourceFragment?.fragmentType && <div>Fragment: {sentenceCase(sourceFragment.fragmentType)}</div>}
              {sourceFragment?.targetTypeHint && <div>Target hint: {sentenceCase(sourceFragment.targetTypeHint)}</div>}
              {candidate.sourceContentHash && <div>Hash: {candidate.sourceContentHash.slice(0, 12)}</div>}
            </div>
          </details>
        </div>
        <div className="rounded-sm border bg-surface p-3">
          <div className="text-xs font-medium text-muted">Daemon evidence</div>
          {runLinks.length === 0 ? (
            <div className="mt-2 text-sm text-muted">No daemon run links recorded yet.</div>
          ) : (
            <div className="mt-2 space-y-2">
              {runLinks.map(({ label, run }) => (
                <Link
                  key={`${label}-${run.runId}`}
                  to={daemonRunPath(run.daemonId, run.runId)}
                  className="flex items-center justify-between rounded-sm border bg-surface-alt px-3 py-2 hover:border-accent"
                >
                  <span>
                    <span className="font-medium text-heading">{label}</span>
                    <span className="ml-2 text-xs text-muted">{run.daemonName ?? `Daemon ${run.daemonId}`}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-accent-text">
                    View evidence <ChevronRight size={13} />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
